import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Popover from '@mui/material/Popover'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import type {
  DataGridProps,
  GridApi,
  GridColumn,
  GridColumnState,
  GridSortModelItem,
} from './types'
import { computeColumnWidths, useGridState, type GridLeafColumn } from './useGridState'
import { useTreeRows } from './useTreeRows'
import { buildTreeGroupColumn } from './GridTreeGroupCell'
import { rowDragColumn } from './rowDrag'
import { useEditing } from './useEditing'
import { useColumnStatePersistence } from './useColumnStatePersistence'
import { useServerSideRows, isLoadingRow, loadingRowId } from './useServerSideRows'
import { applySetFilters, isSetFilterActive } from './filters/setFilterModel'
import {
  findEditableCellInDirection,
  findNextEditableCell,
  type GridCellPosition,
  type GridDirection,
} from './tabNavigation'
import SetFilterPopover from './filters/SetFilterPopover'
import GridHeaderCell from './GridHeaderCell'
import GridBody from './GridBody'
import PinnedBottomRow from './PinnedBottomRow'
import GridLoadingOverlay from './GridLoadingOverlay'
import GridEmptyState from './GridEmptyState'
import GridSkeletonRow from './GridSkeletonRow'
import useColumnWindow from './useColumnWindow'
import { toHeaderCase } from './headerCase'
import useViewportBoundHeight from './useViewportBoundHeight'
import classes from './grid-theme.module.scss'

/**
 * Shared class names for pages that mark their own cells — the replacements for
 * `.ag-cell-editable`, `.ag-cell-non-editable` and `.ag-cell-changed`.
 */
export const gridCellClasses = {
  editable: classes.cellEditable,
  nonEditable: classes.cellNonEditable,
  changed: classes.cellChanged,
  /** A staged value that has just been written — replaces the amber. */
  saved: classes.cellSaved,
  /** Contents shown only while the row is hovered or keyboard-focused. */
  hoverReveal: classes.cellHoverReveal,
  /** A cell a save refused to write — a missing required value. */
  invalid: classes.cellInvalid,
  /** Centres the value (and its editor), overriding `numeric` alignment. */
  centered: classes.cellCentered,
  /**
   * SCRUM-1032 QC item 5 — marks an editable cell as list-backed, with a
   * chevron drawn at rest rather than only once the cell is focused.
   *
   * Opt-in per column, not automatic on `editor: 'select'`: the Station and
   * Controllers grids already draw their own, and a second one from here would
   * double up. Reach for this on a select column that has no affordance yet.
   */
  dropdown: classes.cellDropdown,
}

/**
 * Shared header class names, for `GridColumn.headerClass`.
 *
 * `textLeft` overrides the right alignment a numeric column gives its header.
 * Marking a column as required is a column flag (`required`), not a class —
 * the mark rides in the label text.
 */
export const gridHeaderClasses = {
  textLeft: classes.headerTextLeft,
}

/**
 * Shared row class names, for `DataGridProps.rowClass`. `new` marks a row the
 * user has staged but not yet saved: the tint covers the whole row, so it reads
 * differently from the per-cell amber of an edited value.
 */
export const gridRowClasses = {
  new: classes.rowNew,
  /** A row the user has already worked on — tint plus a left accent bar. */
  touched: classes.rowTouched,
  /** A row written to the server this sitting. */
  saved: classes.rowSaved,
  /** A row whose underlying record no longer matches what the row says it is. */
  conflict: classes.rowConflict,
}

/**
 * A row's staged edit with `fields` dropped — the whole row when omitted. With
 * `expected`, a field goes only while it still holds that value, so a save
 * that passes what it sent keeps a value typed while it was out.
 */
export const withoutPendingEdit = <T,>(
  prev: Map<string, Partial<T>>,
  rowId: string,
  fields?: string[],
  expected?: Partial<T>,
): Map<string, Partial<T>> => {
  if (!prev.has(rowId)) return prev
  const next = new Map(prev)
  if (!fields) {
    next.delete(rowId)
    return next
  }
  const edit = { ...(next.get(rowId) as Record<string, unknown>) }
  const sent = expected as Record<string, unknown> | undefined
  fields.forEach((field) => {
    if (sent && !Object.is(edit[field], sent[field])) return
    delete edit[field]
  })
  if (Object.keys(edit).length === 0) next.delete(rowId)
  else next.set(rowId, edit as Partial<T>)
  return next
}

const DEFAULT_ROW_HEIGHT = 40
const DEFAULT_HEADER_HEIGHT = 42
const DEFAULT_GROUP_HEADER_HEIGHT = 30
const DEFAULT_BLOCK_SIZE = 100

/** Leading 1-based index column (`showRowNumbers`). */
const rowNumberColumn = <T,>(): GridColumn<T> => ({
  field: '__rowNumber',
  headerName: '#',
  width: 52,
  minWidth: 52,
  maxWidth: 52,
  pinned: 'left',
  lockPosition: true,
  sortable: false,
  resizable: false,
  editable: false,
  cellRenderer: ({ rowIndex }) => <>{rowIndex + 1}</>,
  cellClass: classes.cellRowNumber,
  headerClass: classes.headerRowNumber,
})

/**
 * Compares a staged value with the one the base row already carries, so an edit
 * that puts the original value back stops counting as a change. Objects are
 * reduced to the label a cell would show (`{ id, name }` sub-areas, `{ id,
 * description }` pipes), because that is what the user actually chose.
 */
const isSameCellValue = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true

  const label = (value: unknown): string => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>
      return String(
        record.name ?? record.description ?? record.emitter ?? record.id ?? '',
      )
    }
    return String(value)
  }

  return label(a) === label(b)
}

/**
 * How far in from a cell's right edge counts as its dropdown caret. Wider than
 * the glyph: it is a pointer target, and missing it means the list does not
 * open when the user plainly asked for it.
 */
const CARET_ZONE_PX = 30

/** Enough shimmering rows to fill a typical panel without looking like data. */
const MAX_SKELETON_ROWS = 12
/** Used to size the skeleton block when the grid is not height-bound. */
const DEFAULT_SKELETON_HEIGHT = 420

const DataGrid = <T,>({
  columns,
  columnGroups,
  groupHeaderHeight = DEFAULT_GROUP_HEADER_HEIGHT,
  rows = [],
  getRowId,
  postSort,
  treeData,
  rowDrag,
  showRowNumbers = false,
  serverDatasource,
  serverBlockSize = DEFAULT_BLOCK_SIZE,
  serverTransformBlock,
  rowHeight = DEFAULT_ROW_HEIGHT,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  wrapHeaderText = false,
  centerContent = false,
  defaultSort,
  autoHeight = false,
  viewportBound,
  viewportBoundGap = 16,
  editMode = 'none',
  batchEdit = false,
  cellFocus = false,
  onCellValueChanged,
  onCellKeyDown,
  pinnedBottomRow,
  onReachEnd,
  isFetchingMore,
  loadAllRows,
  columnStateStorageKey,
  columnStateLegacyKeys,
  columnStateUnprefixed,
  onFilterChanged,
  onSortChanged,
  sortOnServer = false,
  onServerCountChanged,
  rowFilter,
  loading,
  loadingBlocksInput,
  emptyState,
  emptyMessage,
  className,
  rowClass,
  apiRef,
}: DataGridProps<T>) => {
  const isDark = useSelector(selectTheme) === mode_type.dark
  const rootRef = useRef<HTMLDivElement>(null)
  const boundMaxHeight = useViewportBoundHeight(
    rootRef,
    viewportBound ?? !autoHeight,
    viewportBoundGap,
  )
  const viewportRef = useRef<HTMLDivElement | null>(null)
  /** Last horizontal scroll position, re-applied when the body is remounted. */
  const scrollLeftRef = useRef(0)
  const headerScrollRef = useRef<HTMLDivElement>(null)
  const pinnedBottomInnerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  /**
   * The body's vertical scrollbar takes width the header viewport does not
   * have, so the two end up with different maximum scrollLeft — the header
   * runs out first and lags behind the columns by exactly the scrollbar's
   * width. Reserving the same gutter on the header keeps them in step.
   */
  const [scrollbarWidth, setScrollbarWidth] = useState(0)

  const measureViewportRef = useRef((root: HTMLElement) => {
    const viewport = viewportRef.current
    setContainerWidth(viewport?.clientWidth || root.clientWidth)
    setScrollbarWidth(viewport ? viewport.offsetWidth - viewport.clientWidth : 0)
  })

  const isServerMode = Boolean(serverDatasource)

  const [filterModel, setFilterModel] = useState<Record<string, unknown>>({})
  const [filterAnchor, setFilterAnchor] = useState<{
    field: string
    element: HTMLElement
  } | null>(null)

  const [pendingEdits, setPendingEdits] = useState<Map<string, Partial<T>>>(new Map())
  const pendingEditsRef = useRef(pendingEdits)
  pendingEditsRef.current = pendingEdits
  const rowsRef = useRef(rows)
  rowsRef.current = rows

  // `batchEdit` renders every row as base ⊕ its staged edit. Rows without an
  // edit keep their identity so the memoised row components do not re-render.
  const editedRows = useMemo(() => {
    if (!batchEdit || pendingEdits.size === 0) return rows
    return rows.map((row) => {
      const edit = pendingEdits.get(getRowId(row))
      return edit ? { ...row, ...edit } : row
    })
  }, [batchEdit, pendingEdits, rows, getRowId])

  // Client mode filters here; server mode carries the model to the datasource
  // instead — a block cache only ever holds part of the result set, so matching
  // rows locally would filter the current page rather than the query.
  const filteredRows = useMemo(() => {
    if (isServerMode) return editedRows
    const setFiltered = applySetFilters(editedRows, columns, filterModel)
    // `rowFilter` is state the columns know nothing about — see its docs. In
    // client mode there is no block cache to keep in step, so it is a plain
    // filter on top of the set filters.
    return rowFilter ? setFiltered.filter(rowFilter) : setFiltered
  }, [isServerMode, editedRows, columns, filterModel, rowFilter])

  // The tree's group column and the drag handle are columns like any other as
  // far as ordering, width and persistence go, so they are declared here rather
  // than spliced in at render time. The group column is the unwrapped one: the
  // chevron wrapper needs the row model, which is not built yet.
  const allColumns = useMemo<GridColumn<T>[]>(
    () => [
      ...(rowDrag?.enabled ? [rowDragColumn<T>()] : []),
      ...(showRowNumbers ? [rowNumberColumn<T>()] : []),
      ...(treeData ? [treeData.groupColumn] : []),
      ...columns,
    ],
    [columns, treeData, rowDrag?.enabled, showRowNumbers],
  )

  const {
    displayedRows: clientRows,
    orderedColumns,
    visibleColumns,
    activeSort,
    getSortDirection,
    toggleSort,
    widthOverrides,
    setWidthOverrides,
    setColumnWidth,
    hiddenFields,
    setHiddenFields,
    setColumnsVisible,
    setColumnOrder,
  } = useGridState<T>({
    columns: allColumns,
    rows: filteredRows,
    getRowId,
    // A tree sorts inside each set of siblings, which the flat row model cannot
    // express; `useTreeRows` owns both the sort and the postSort in that mode.
    postSort: treeData ? undefined : postSort,
    sortOnServer: isServerMode || Boolean(treeData) || sortOnServer,
    defaultSort,
  })

  const tree = useTreeRows<T>({
    rows: filteredRows,
    columns: visibleColumns,
    getRowId,
    treeData,
    sort: activeSort,
    postSort,
  })

  // `sortField` lets a column sort on a key the API knows but the row does not
  // carry under that name (Controllers sorts the merged address column on
  // `address`).
  const sortModel = useMemo<GridSortModelItem[]>(() => {
    if (!activeSort) return []
    const column = orderedColumns.find((col) => col.field === activeSort.field)
    return [
      {
        field: column?.sortField ?? activeSort.field,
        sort: activeSort.desc ? 'desc' : 'asc',
      },
    ]
  }, [activeSort, orderedColumns])

  const handlePurge = useCallback(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = 0
  }, [])

  const server = useServerSideRows<T>({
    datasource: serverDatasource,
    blockSize: serverBlockSize,
    sortModel,
    filterModel,
    transformBlock: serverTransformBlock,
    onPurge: handlePurge,
  })

  const serverRows = server.rows

  // A row hidden here is still a row the block cache has paged into a fixed
  // position, so the source index of every displayed row is kept alongside:
  // the visible range the body reports is an index into the *filtered* list,
  // and asking the datasource for that range would fetch the wrong blocks.
  const { filteredServerRows, sourceIndexes } = useMemo(() => {
    if (!isServerMode || !rowFilter) {
      return { filteredServerRows: serverRows, sourceIndexes: null }
    }
    const rowsOut: T[] = []
    const indexes: number[] = []
    serverRows.forEach((row, index) => {
      // Placeholders are what the user scrolls into to trigger the next fetch;
      // filtering them out would stop the list growing.
      if (isLoadingRow(row) || rowFilter(row)) {
        rowsOut.push(row)
        indexes.push(index)
      }
    })
    return { filteredServerRows: rowsOut, sourceIndexes: indexes }
  }, [isServerMode, rowFilter, serverRows])

  const handleVisibleRangeChange = useCallback(
    (startIndex: number, endIndex: number) => {
      if (!sourceIndexes) {
        serverRef.current.ensureRange(startIndex, endIndex)
        return
      }
      const first = sourceIndexes[startIndex] ?? 0
      const last = sourceIndexes[Math.min(endIndex, sourceIndexes.length - 1)] ?? first
      serverRef.current.ensureRange(first, last)
    },
    [sourceIndexes],
  )

  const baseDisplayedRows = isServerMode
    ? filteredServerRows
    : treeData
      ? tree.rows
      : clientRows

  // `batchEdit` renders base ⊕ staged edit. The client path already does this
  // (`editedRows`, above); the server path never did, so a committed edit was
  // held in the buffer but not shown — the cell kept its old value until a
  // block refetch happened to bring the saved one back.
  const displayedRows = useMemo(() => {
    if (!isServerMode || !batchEdit || pendingEdits.size === 0) {
      return baseDisplayedRows
    }
    return baseDisplayedRows.map((row) => {
      const edit = pendingEdits.get(getRowId(row))
      return edit ? { ...row, ...edit } : row
    })
  }, [isServerMode, batchEdit, pendingEdits, baseDisplayedRows, getRowId])

  // The commit guards below need the row as it is *now*, not the copy the cell
  // closed over when it rendered.
  const displayedRowsRef = useRef(displayedRows)
  displayedRowsRef.current = displayedRows
  // Unstaged, for deciding whether an edit puts a value back to what the server
  // holds — comparing against the staged copy would call every edit a change.
  const baseDisplayedRowsRef = useRef(baseDisplayedRows)
  baseDisplayedRowsRef.current = baseDisplayedRows

  // Read through refs so the public API object does not have to be rebuilt (and
  // every consumer re-run) each time a block lands or a filter changes.
  const filterModelRef = useRef(filterModel)
  filterModelRef.current = filterModel
  const serverRef = useRef(server)
  serverRef.current = server

  const [focusedCell, setFocusedCell] = useState<{
    rowId: string
    field: string
  } | null>(null)

  const {
    editingCell,
    startEditing,
    stopEditing,
    isEditing,
    isEditingAny,
    getEditingAny,
    handleCellClick,
  } = useEditing()

  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return

    // Measure the scrolling viewport, not the root: once the rows overflow
    // vertically its scrollbar takes ~15px that the columns cannot use, and
    // laying them out against the root's width pushed the last column off the
    // right edge by exactly that much.
    const measure = () => measureViewportRef.current(el)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(el)
    if (viewportRef.current) observer.observe(viewportRef.current)
    return () => observer.disconnect()
  }, [])

  // --- Column layout -------------------------------------------------------

  // The group column is only wrapped once the row model exists: the chevron
  // and the indent are drawn from each row's place in the tree.
  const renderColumns = useMemo<GridColumn<T>[]>(() => {
    if (!treeData) return visibleColumns
    const wrapped = buildTreeGroupColumn<T>({
      treeData,
      metaById: tree.metaById,
      onToggle: tree.setExpanded,
      isDark,
    })
    return visibleColumns.map((col) =>
      col.field === treeData.groupColumn.field ? wrapped : col,
    )
  }, [visibleColumns, treeData, tree.metaById, tree.setExpanded, isDark])

  const leafColumns = useMemo<GridLeafColumn<T>[]>(() => {
    const left = renderColumns.filter((c) => c.pinned === 'left')
    const right = renderColumns.filter((c) => c.pinned === 'right')
    const middle = renderColumns.filter((c) => !c.pinned)
    const ordered = [...left, ...middle, ...right]

    const widths = computeColumnWidths(ordered, containerWidth, widthOverrides)

    let leftOffset = 0
    const leftOffsets = new Map<string, number>()
    left.forEach((col) => {
      leftOffsets.set(col.field, leftOffset)
      leftOffset += widths[ordered.indexOf(col)]
    })

    let rightOffset = 0
    const rightOffsets = new Map<string, number>()
    ;[...right].reverse().forEach((col) => {
      rightOffsets.set(col.field, rightOffset)
      rightOffset += widths[ordered.indexOf(col)]
    })

    return ordered.map((col, index) => ({
      column: col,
      field: col.field,
      width: widths[index],
      offset:
        col.pinned === 'left'
          ? (leftOffsets.get(col.field) ?? 0)
          : col.pinned === 'right'
            ? (rightOffsets.get(col.field) ?? 0)
            : 0,
      pinned: col.pinned,
      isPinnedEdge:
        (col.pinned === 'left' && col === left[left.length - 1]) ||
        (col.pinned === 'right' && col === right[0]),
    }))
  }, [renderColumns, containerWidth, widthOverrides])

  const totalWidth = useMemo(
    () => leafColumns.reduce((sum, leaf) => sum + leaf.width, 0),
    [leafColumns],
  )

  // Horizontal virtualization: a wide grid renders only the columns in view.
  const { window: columnWindow, onScrollLeft } = useColumnWindow(
    leafColumns,
    containerWidth,
  )
  const renderedColumns = columnWindow.columns

  // Group bands span the run of adjacent displayed columns that name the same
  // group. Hiding or reordering a column therefore reshapes the band with it,
  // and columns belonging to no group sit under a blank one so the two header
  // rows stay aligned.
  const headerGroupBands = useMemo(() => {
    if (!columnGroups) return []

    const byId = new Map(columnGroups.map((group) => [group.id, group]))
    const bands: {
      key: string
      headerName: string
      headerClass?: string
      width: number
      offset: number
      pinned?: 'left' | 'right'
    }[] = []

    leafColumns.forEach((leaf) => {
      const group = leaf.column.group ? byId.get(leaf.column.group) : undefined
      const previous = bands[bands.length - 1]
      const key = group ? `${group.id}:${leaf.pinned ?? ''}` : `:${leaf.pinned ?? ''}`

      if (previous && previous.key === key) {
        previous.width += leaf.width
        previous.offset = Math.min(previous.offset, leaf.offset)
        return
      }

      bands.push({
        key,
        headerName: group?.headerName ?? '',
        headerClass: group?.headerClass,
        width: leaf.width,
        offset: leaf.offset,
        pinned: leaf.pinned,
      })
    })

    return bands
  }, [columnGroups, leafColumns])

  // --- Public API ----------------------------------------------------------

  const apiValue = useMemo<GridApi<T>>(
    () => ({
      getRow: (rowId) => displayedRows.find((row) => getRowId(row) === rowId),
      getDisplayedRows: () => displayedRows,
      refreshCells: () => setPendingEdits((prev) => new Map(prev)),

      startEditingCell: (rowId, field) => startEditing(rowId, field),
      stopEditing: () => stopEditing(),
      isEditingCell: () => isEditingAny(),
      getEditingCell: () => getEditingAny(),

      getPendingEdits: () => pendingEditsRef.current,
      setPendingEdit: (rowId, edit) =>
        setPendingEdits((prev) => {
          const next = new Map(prev)
          next.set(rowId, { ...(next.get(rowId) ?? {}), ...edit })
          return next
        }),
      removePendingEdit: (rowId, fields, expected) =>
        setPendingEdits((prev) =>
          withoutPendingEdit(prev, rowId, fields, expected),
        ),
      clearPendingEdits: () => setPendingEdits(new Map()),

      // Every column, hidden ones included: a settings popover has to be able to
      // offer a column back after it has been hidden.
      getColumnState: () =>
        orderedColumns.map((col) => ({
          colId: col.field,
          width: widthOverrides[col.field] ?? col.width,
          hide: Boolean(col.hide || hiddenFields[col.field]),
          pinned: col.pinned ?? null,
        })),
      applyColumnState: (state: GridColumnState[], options) => {
        const {
          applyOrder = true,
          applyWidth = true,
          applyVisibility = true,
        } = options ?? {}

        if (applyOrder) setColumnOrder(state.map((entry) => entry.colId))

        if (applyWidth) {
          const widths: Record<string, number> = {}
          state.forEach((entry) => {
            if (typeof entry.width === 'number') widths[entry.colId] = entry.width
          })
          setWidthOverrides((prev) => ({ ...prev, ...widths }))
        }

        if (applyVisibility) {
          const hidden: Record<string, boolean> = {}
          state.forEach((entry) => {
            if (entry.hide) hidden[entry.colId] = true
          })
          setHiddenFields(hidden)
        }
      },
      setColumnsVisible,

      getFilterModel: () => filterModelRef.current,
      setFilterModel: (model) => setFilterModel(model),

      // Row selection lands in Phase D; the surface is declared now so the
      // ports do not have to change shape later.
      getSelectedRowIds: () => [],
      setSelectedRowIds: () => undefined,
      setExpanded: tree.setExpanded,
      expandAll: tree.expandAll,
      collapseAll: tree.collapseAll,
      refreshServerSide: (options) => serverRef.current.refresh(options),

      // Centres the row the way ag-grid's `ensureNodeVisible(node, 'middle')`
      // did — a row scrolled to the very top of the viewport reads as cut off.
      scrollToRow: (rowId) => {
        const index = displayedRows.findIndex((row) => getRowId(row) === rowId)
        const viewport = viewportRef.current
        if (index < 0 || !viewport) return
        const target = index * rowHeight - (viewport.clientHeight - rowHeight) / 2
        viewport.scrollTop = Math.max(0, target)
      },
      getViewportEl: () => viewportRef.current,
    }),
    [
      displayedRows,
      getRowId,
      startEditing,
      stopEditing,
      isEditingAny,
      getEditingAny,
      orderedColumns,
      widthOverrides,
      hiddenFields,
      setWidthOverrides,
      setHiddenFields,
      setColumnsVisible,
      setColumnOrder,
      rowHeight,
      tree.setExpanded,
      tree.expandAll,
      tree.collapseAll,
    ],
  )

  // --- Editing -------------------------------------------------------------

  const handleCommit = useCallback(
    (
      rowId: string,
      column: GridColumn<T>,
      row: T,
      value: unknown,
      baseline?: unknown,
    ) => {
      // Only close the editor this commit came from. An editor commits on
      // teardown as well as on Enter and blur, so when the keyboard moves to
      // the next cell the outgoing one commits *after* the new editor has
      // opened — and closing unconditionally here shut that new editor again,
      // leaving the arrow keys looking like they did nothing.
      if (isEditing(rowId, column.field)) stopEditing()

      // An editor commits on teardown as well as on Enter and blur, and a row
      // can be replaced underneath it at any time: a block refetch, a live
      // update, a filter change. Two invariants keep a late commit from landing
      // somewhere it was never typed.
      //
      // 1. The row must still be there. A commit for a row that has scrolled
      //    out of the block cache has nothing to apply to.
      const current = displayedRowsRef.current.find(
        (candidate) => getRowId(candidate) === rowId,
      )
      if (!current) return

      const oldValue = column.valueGetter
        ? column.valueGetter(current)
        : (current as Record<string, unknown>)[column.field]

      // 2. An editor may only overwrite the value it was opened on. If the cell
      //    holds something else now, this editor is stale — it was opened
      //    against different data — and writing its draft would put one row's
      //    text into another row. Silent by design: there is nothing the user
      //    did wrong and nothing for them to redo, the edit never applied.
      if (baseline !== undefined && !isSameCellValue(baseline, oldValue)) return

      if (isSameCellValue(oldValue, value)) return

      const result = onCellValueChanged?.({
        // The current row, not the one the cell captured: pages key their own
        // edit buffers off `event.row`, and a stale object keys them wrongly.
        row: current,
        rowId,
        field: column.field,
        oldValue,
        newValue: value,
        api: apiValue,
      })

      // `false` rejects the edit: the row keeps its old value, nothing is staged.
      if (result === false) return

      const patch = column.valueSetter
        ? column.valueSetter(current, value)
        : ({ [column.field]: value } as Partial<T>)

      if (!patch || Object.keys(patch).length === 0) return

      setPendingEdits((prev) => {
        const merged = {
          ...(prev.get(rowId) as Record<string, unknown> | undefined),
          ...(patch as Record<string, unknown>),
        }

        // An edit that restores the base value is no longer a pending change —
        // drop it, and the row with it once nothing of it is left, so neither
        // the amber highlight nor the save diff reports a change that is not one.
        //
        // Compared against the UNTOUCHED rows, which is not what the grid
        // displays: in client mode `baseDisplayedRows` is already base ⊕ staged
        // edits, so reading the base from there made every staged value look
        // like the original. The second edit to a row then found the first one
        // "unchanged", dropped it from the patch, and the cell reverted — the
        // edit before last vanishing as the next one landed.
        //
        // A row not found (tree mode builds its own) prunes nothing: at worst a
        // no-op edit is saved, where the alternative is losing a real one.
        const baseRow = batchEdit
          ? ((isServerMode ? baseDisplayedRowsRef.current : rowsRef.current).find(
              (candidate) => getRowId(candidate) === rowId,
            ) as Record<string, unknown> | undefined)
          : undefined
        if (baseRow) {
          /**
           * Only the fields this commit touched.
           *
           * SCRUM-1032 QC round 6, item 2 — re-testing every staged field was
           * wrong whenever the `rows` prop is itself derived from a page-level
           * edit buffer. The guided decoder list passes `guidedRows`, which is
           * the server list with its own staged edits substituted in, so after
           * a rename the base row already reads "RENAMED": the next edit to
           * that row found the rename "unchanged" and dropped it from here.
           * The value then survived only in the page's buffer, and reappeared
           * as the old one the moment that buffer was retired mid-save —
           * "during saving → name of the decoder return to the previous".
           *
           * The question this answers is "did the user just type the value
           * that was already there", and that is only ever about the field
           * they typed in.
           */
          Object.keys(patch as Record<string, unknown>).forEach((field) => {
            if (
              field in merged &&
              isSameCellValue(merged[field], baseRow[field])
            ) {
              delete merged[field]
            }
          })
        }

        const next = new Map(prev)
        if (Object.keys(merged).length === 0) next.delete(rowId)
        else next.set(rowId, merged as Partial<T>)
        return next
      })
    },
    [onCellValueChanged, stopEditing, isEditing, apiValue, getRowId, batchEdit, isServerMode],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, rowId: string, field: string, row: T) => {
      if (!onCellKeyDown) return
      onCellKeyDown({
        row,
        rowId,
        field,
        key: event.key,
        preventDefault: () => event.preventDefault(),
      })
    },
    [onCellKeyDown],
  )

  const handleCellClickWithMode = useCallback(
    (event: React.MouseEvent, rowId: string, field: string, editable: boolean) => {
      if (cellFocus) setFocusedCell({ rowId, field })
      if (editMode !== 'singleClick') return
      // A dropdown cell opens its list only when the caret itself is clicked.
      // Clicking the value selects the cell — the list dropping open over the
      // rows below on every click made the grid hard to read while working
      // down it, and hid the row the user was about to reach.
      const cell = event.currentTarget as HTMLElement | null
      const rect = cell?.getBoundingClientRect()
      const openList = rect
        ? rect.right - event.clientX <= CARET_ZONE_PX
        : false
      handleCellClick(event, rowId, field, editable, openList)
    },
    [editMode, handleCellClick, cellFocus],
  )

  // --- Cell focus + arrow-key navigation -----------------------------------
  // One cell at a time: a focus ring the arrows move, and Enter to edit it.
  // There is no range selection — nothing in this app read one.

  /**
   * Whether this cell can actually be typed into: the column needs an editor,
   * and `editable` is resolved per cell because a column can hand back a
   * different answer per row (a non-irrigation station, a chip row, a draft).
   */
  const isCellEditable = useCallback(
    (column: GridColumn<T> | undefined, rowIndex: number): boolean => {
      // Through the ref: a deferred fill asks about rows that arrived after
      // this callback was created.
      const row = displayedRowsRef.current[rowIndex]
      if (!column?.editor || !row) return false
      // A placeholder for a block that has not arrived is not a row. Editing
      // one staged an edit with no id — every placeholder under the same
      // blank key — and the save sent it as a real record, which the server
      // refused. Fill skips it through here, and so does the keyboard.
      if (isServerMode && isLoadingRow(row)) return false
      if (typeof column.editable !== 'function') return Boolean(column.editable)
      return column.editable({
        row,
        rowId: getRowId(row),
        value: column.valueGetter
          ? column.valueGetter(row)
          : (row as Record<string, unknown>)[column.field],
        rowIndex,
        isPinnedBottom: false,
        api: apiValue,
        depth: 0,
      })
    },
    [getRowId, apiValue, isServerMode],
  )

  /** Opens a cell found by keyboard navigation, scrolling to it if it is off-screen. */
  const openCellAt = useCallback(
    (target: GridCellPosition, fromRowIndex: number) => {
      const rowId = getRowId(displayedRows[target.rowIndex])
      const field = leafColumns[target.colIndex].field
      setFocusedCell({ rowId, field })
      // Opening the next editor unmounts the current one, and that teardown is
      // what commits what was typed.
      startEditing(rowId, field)
      if (target.rowIndex !== fromRowIndex) apiValue.scrollToRow(rowId)
    },
    [displayedRows, getRowId, leafColumns, startEditing, apiValue],
  )

  /**
   * Whether the arrow keys belong to the grid or to whatever is open in the
   * cell. An arrow inside an editor already means something: it walks the caret
   * through the text, and it picks options in an open dropdown. The grid only
   * takes the key when the editor has no use left for it — the caret sitting at
   * the end of the text it is about to leave.
   */
  const arrowBelongsToGrid = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, direction: GridDirection) => {
      if (!editingCell) return false

      // Alt+Arrow is the editor's own shortcut for opening its list.
      if (event.altKey) return false

      const target = event.target as HTMLElement | null
      const input =
        target instanceof HTMLInputElement && target.type !== 'checkbox'
          ? target
          : null
      // A select's list is driven by the same arrows; leave it alone until it
      // is closed.
      if (!input) return false
      if (document.querySelector('[role="listbox"]')) return false

      if (direction === 'up' || direction === 'down') return true

      // `type="number"` has no caret to read: Chrome answers null for
      // selectionStart and Safari throws outright. There is no "walk to the end
      // of the text first" state to wait for, so the grid takes the arrow
      // straight away — which is what the numeric grids (ET Reference, Emitter,
      // and Hydraulic's capacity) need to be navigable at all.
      let selectionStart: number | null = null
      let selectionEnd: number | null = null
      try {
        selectionStart = input.selectionStart
        selectionEnd = input.selectionEnd
      } catch {
        return true
      }
      if (selectionStart === null || selectionEnd === null) return true

      // Any selected text means the arrow is about to collapse it, not leave.
      if (selectionStart !== selectionEnd) return false
      return direction === 'left'
        ? selectionStart === 0
        : selectionStart === input.value.length
    },
    [editingCell],
  )

  /**
   * Arrow keys walk the editable cells while one of them is open: up and down
   * within the column, left and right within the row. Entering a long list of
   * decoders is columns of short values, and reaching for the mouse between
   * each one is the slow part.
   */
  const handleArrowNavigation = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, direction: GridDirection) => {
      if (editMode !== 'singleClick' || !editingCell) return
      if (!arrowBelongsToGrid(event, direction)) return

      const rowIndex = displayedRows.findIndex(
        (row) => getRowId(row) === editingCell.rowId,
      )
      const colIndex = leafColumns.findIndex(
        (leaf) => leaf.field === editingCell.field,
      )
      if (rowIndex < 0 || colIndex < 0) return

      const next = findEditableCellInDirection({
        from: { rowIndex, colIndex },
        direction,
        rowCount: displayedRows.length,
        colCount: leafColumns.length,
        isEditable: (row, col) => isCellEditable(leafColumns[col]?.column, row),
      })
      if (!next) return

      // Held back until there is somewhere to go: on a number input the arrow
      // would otherwise have already nudged the value.
      event.preventDefault()
      openCellAt(next, rowIndex)
    },
    [
      editMode,
      editingCell,
      arrowBelongsToGrid,
      displayedRows,
      getRowId,
      leafColumns,
      isCellEditable,
      openCellAt,
    ],
  )

  /**
   * Enter commits and drops to the cell below, the way a spreadsheet does.
   * Entering a column is the common shape of this work — a purpose, a name, a
   * capacity, row after row — and stopping dead on commit made every row cost a
   * click. The editor has already committed by the time this runs: it handles
   * Enter itself, and the event reaches the grid afterwards.
   */
  const handleEnterNavigation = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (editMode !== 'singleClick' || !editingCell) return

      const rowIndex = displayedRows.findIndex(
        (row) => getRowId(row) === editingCell.rowId,
      )
      const colIndex = leafColumns.findIndex(
        (leaf) => leaf.field === editingCell.field,
      )
      if (rowIndex < 0 || colIndex < 0) return

      const next = findEditableCellInDirection({
        from: { rowIndex, colIndex },
        direction: 'down',
        rowCount: displayedRows.length,
        colCount: leafColumns.length,
        isEditable: (row, col) => isCellEditable(leafColumns[col]?.column, row),
      })
      // The last row commits and closes, rather than trapping the user in it.
      if (!next) return

      event.preventDefault()
      openCellAt(next, rowIndex)
    },
    [
      editMode,
      editingCell,
      displayedRows,
      getRowId,
      leafColumns,
      isCellEditable,
      openCellAt,
    ],
  )

  /**
   * Drag-to-fill — the square in the corner of the cell being edited.
   *
   * Down a long list the rows below usually want what the row above has: the
   * same purpose, the same configuration. Dragging the corner over them says
   * so directly, which is the gesture people already know from a spreadsheet,
   * and unlike a keyboard shortcut it is visible.
   */
  const [fillRange, setFillRange] = useState<{
    field: string
    fromRowIndex: number
    toRowIndex: number
  } | null>(null)
  /** Where to put the "N rows" badge — the pointer, while it is dragging. */
  const [fillPointer, setFillPointer] = useState<{ x: number; y: number } | null>(
    null,
  )
  const fillRangeRef = useRef(fillRange)
  fillRangeRef.current = fillRange

  /** Writes one column's value from the source row across a run of rows. */
  const applyFill = useCallback(
    (field: string, fromRowIndex: number, toRowIndex: number) => {
      const column = leafColumns.find((leaf) => leaf.field === field)?.column
      // Read through the ref, not the closure: a fill that waited for the rest
      // of the list runs after more rows have arrived, and the array this
      // callback captured is the short one it was created with — which is what
      // made "fill to the end" stop where the list had been.
      const rows = displayedRowsRef.current
      const source = rows[fromRowIndex]
      if (!column || !source || toRowIndex === fromRowIndex) return

      const valueOf = (row: T) =>
        column.valueGetter
          ? column.valueGetter(row)
          : (row as Record<string, unknown>)[column.field]

      const value = valueOf(source)
      const step = toRowIndex > fromRowIndex ? 1 : -1

      for (
        let rowIndex = fromRowIndex + step;
        step > 0 ? rowIndex <= toRowIndex : rowIndex >= toRowIndex;
        rowIndex += step
      ) {
        const target = rows[rowIndex]
        // A row that cannot take the value is stepped over rather than
        // stopping the fill: a chip row in the middle of a run should not end
        // a drag the user clearly meant to cover it.
        if (!target || !isCellEditable(column, rowIndex)) continue
        const existing = valueOf(target)
        if (isSameCellValue(existing, value)) continue
        // Through the normal commit path, so the page's own buffer, the changed
        // highlight and autosave all see each one as the edit it is.
        handleCommit(getRowId(target), column, target, value, existing)
      }
    },
    [leafColumns, isCellEditable, handleCommit, getRowId],
  )

  /** The row under the pointer, read from the DOM so virtualization is moot. */
  const rowIndexAtPoint = useCallback((clientY: number) => {
    const viewport = viewportRef.current
    if (!viewport) return null
    const bounded = Math.min(
      Math.max(clientY, viewport.getBoundingClientRect().top + 1),
      viewport.getBoundingClientRect().bottom - 1,
    )
    const element = document
      .elementFromPoint(viewport.getBoundingClientRect().left + 8, bounded)
      ?.closest('[data-row-index]')
    const index = element?.getAttribute('data-row-index')
    return index == null ? null : Number(index)
  }, [])

  const handleFillHandlePointerDown = useCallback(
    (event: React.PointerEvent, field: string) => {
      if (editMode !== 'singleClick') return
      const current = editingCell ?? focusedCell
      if (!current || current.field !== field) return
      const fromRowIndex = displayedRows.findIndex(
        (row) => getRowId(row) === current.rowId,
      )
      if (fromRowIndex < 0) return

      // The handle is a drag, not a click into the cell underneath it.
      event.preventDefault()
      event.stopPropagation()
      setFillRange({ field, fromRowIndex, toRowIndex: fromRowIndex })
      setFillPointer({ x: event.clientX, y: event.clientY })
    },
    [editMode, editingCell, focusedCell, displayedRows, getRowId],
  )

  /**
   * A double-click fill waiting on the rest of the list. Keyed by row id, not
   * index, and applied from the rows as they are once everything has arrived —
   * the handler's own `displayedRows` is the short list it started with.
   */
  const [pendingFill, setPendingFill] = useState<{
    field: string
    rowId: string
  } | null>(null)

  /**
   * Excel's other fill gesture: double-clicking the handle fills the column
   * from here to the last row.
   *
   * Dragging is fine for a screenful. It is not a plan for a list built from a
   * thousand decoders, where the same area or emitter belongs on every row and
   * the drag would be a minute of holding the mouse against the bottom edge.
   */
  const handleFillHandleDoubleClick = useCallback(
    (event: React.MouseEvent, field: string) => {
      if (editMode !== 'singleClick') return
      event.preventDefault()
      event.stopPropagation()

      const current = editingCell ?? focusedCell
      if (!current) return
      const fromRowIndex = displayedRows.findIndex(
        (row) => getRowId(row) === current.rowId,
      )
      if (fromRowIndex < 0) return

      // The cell may still be open with an uncommitted value; commit it first
      // so the fill copies what the user sees rather than what it replaced.
      stopEditing()

      // "To the end" means the end of the list, not the end of what has been
      // scrolled in. A paged grid holds only its loaded pages (plus a block of
      // placeholders), so the fill used to stop there without a word — QC saw
      // it "stop halfway" down the decoders. The rest is loaded first, and the
      // fill runs from the effect below once it has arrived.
      const serverHasMore =
        isServerMode &&
        server.totalCount != null &&
        serverRef.current.rows.length < server.totalCount
      if (serverHasMore) {
        setPendingFill({ field, rowId: current.rowId })
        serverRef.current.ensureRange(fromRowIndex, (server.totalCount ?? 1) - 1)
        return
      }
      if (!isServerMode && loadAllRows) {
        const rowId = current.rowId
        setPendingFill({ field, rowId })
        loadAllRows()
          .catch(() => undefined)
          .finally(() => {
            // A tick's grace. `loadAllRows` resolves once the *page* holds
            // every row, which is still a render before they reach the grid as
            // props — filling right here copied into the short list again, and
            // the tail of the station list kept its old value.
            window.setTimeout(() => {
              const rows = displayedRowsRef.current
              const from = rows.findIndex((row) => getRowId(row) === rowId)
              setPendingFill(null)
              if (from < 0 || rows.length - 1 <= from) return
              applyFill(field, from, rows.length - 1)
            }, 250)
          })
        return
      }

      const lastRowIndex = displayedRows.length - 1
      if (lastRowIndex <= fromRowIndex) return
      window.setTimeout(() => applyFill(field, fromRowIndex, lastRowIndex), 0)
    },
    [
      editMode,
      editingCell,
      focusedCell,
      displayedRows,
      getRowId,
      stopEditing,
      applyFill,
      isServerMode,
      server.totalCount,
      loadAllRows,
    ],
  )

  useEffect(() => {
    if (!pendingFill) return
    // Client rows are filled by the handler once `loadAllRows` has settled;
    // this effect is the server path, which waits on the block cache instead.
    if (!isServerMode) return

    const stillLoading = displayedRows.some((row) => isLoadingRow(row))
    const complete =
      server.totalCount != null &&
      server.rows.length >= server.totalCount &&
      !stillLoading
    // Every fetch has settled but a block is still missing: one failed. Fill
    // what did arrive rather than hold the grid behind an overlay forever.
    const gaveUp = !server.isLoading && stillLoading
    if (!complete && !gaveUp) return

    const fromRowIndex = displayedRows.findIndex(
      (row) => getRowId(row) === pendingFill.rowId,
    )
    setPendingFill(null)
    if (fromRowIndex < 0) return
    applyFill(pendingFill.field, fromRowIndex, displayedRows.length - 1)
  }, [
    pendingFill,
    isServerMode,
    displayedRows,
    server.totalCount,
    server.rows.length,
    server.isLoading,
    getRowId,
    applyFill,
  ])

  // The drag is tracked on the window: the pointer leaves the handle
  // immediately, and a release outside the grid still has to end it.
  useEffect(() => {
    if (!fillRange) return

    const onMove = (event: PointerEvent) => {
      setFillPointer({ x: event.clientX, y: event.clientY })
      const rowIndex = rowIndexAtPoint(event.clientY)
      if (rowIndex == null) return
      setFillRange((previous) =>
        previous && previous.toRowIndex !== rowIndex
          ? { ...previous, toRowIndex: rowIndex }
          : previous,
      )
    }

    const onUp = () => {
      const range = fillRangeRef.current
      setFillRange(null)
      setFillPointer(null)
      if (range) applyFill(range.field, range.fromRowIndex, range.toRowIndex)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [fillRange, rowIndexAtPoint, applyFill])

  /** The cell wearing the handle: the one being edited, or the focused one. */
  const fillHandleCell = useMemo(() => {
    if (editMode !== 'singleClick') return null
    const current = editingCell ?? (cellFocus ? focusedCell : null)
    if (!current) return null
    const rowIndex = displayedRows.findIndex(
      (row) => getRowId(row) === current.rowId,
    )
    const column = leafColumns.find(
      (leaf) => leaf.field === current.field,
    )?.column
    // Only where there is something to drag: the column has to be one this
    // row can be edited in, and there has to be a row below to fill into.
    if (rowIndex < 0 || !isCellEditable(column, rowIndex)) return null
    return current
  }, [
    editMode,
    editingCell,
    focusedCell,
    cellFocus,
    displayedRows,
    getRowId,
    leafColumns,
    isCellEditable,
  ])

  /**
   * Tab moves to the next editable cell and opens it, so a row can be filled in
   * without reaching for the mouse. It works while an editor is open — which is
   * most of the time in a bulk-edit grid — and there the outgoing editor
   * commits on its way out, the same as it does on blur.
   */
  const handleTabNavigation = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (editMode !== 'singleClick') return
      const current = editingCell ?? focusedCell
      if (!current) return

      const rowIndex = displayedRows.findIndex(
        (row) => getRowId(row) === current.rowId,
      )
      const colIndex = leafColumns.findIndex(
        (leaf) => leaf.field === current.field,
      )
      if (rowIndex < 0 || colIndex < 0) return

      const next = findNextEditableCell({
        from: { rowIndex, colIndex },
        step: event.shiftKey ? -1 : 1,
        rowCount: displayedRows.length,
        colCount: leafColumns.length,
        isEditable: (row, col) => isCellEditable(leafColumns[col]?.column, row),
      })
      // Nothing editable left in that direction: let the browser have the Tab
      // so focus leaves the grid instead of being swallowed.
      if (!next) return

      event.preventDefault()
      openCellAt(next, rowIndex)
    },
    [
      editMode,
      editingCell,
      focusedCell,
      displayedRows,
      getRowId,
      leafColumns,
      isCellEditable,
      openCellAt,
    ],
  )

  const handleGridKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Tab is handled whether or not a cell is being edited, and without
      // `cellFocus`: a grid you can type into is one you should be able to tab
      // through. Everything below it is focus-ring navigation, which is not.
      if (event.key === 'Tab') {
        handleTabNavigation(event)
        return
      }
      // The editor has already committed on Enter by the time this runs; what
      // is left is where to go next.
      if (editingCell && event.key === 'Enter') {
        handleEnterNavigation(event)
        return
      }
      // While a cell is open the arrows walk the editable cells; the focus-ring
      // navigation below is for when none is.
      if (editingCell) {
        switch (event.key) {
          case 'ArrowUp':
            handleArrowNavigation(event, 'up')
            return
          case 'ArrowDown':
            handleArrowNavigation(event, 'down')
            return
          case 'ArrowLeft':
            handleArrowNavigation(event, 'left')
            return
          case 'ArrowRight':
            handleArrowNavigation(event, 'right')
            return
          default:
            break
        }
      }
      if (!cellFocus || editingCell) return

      const fields = leafColumns.map((leaf) => leaf.field)
      const current = focusedCell
      const rowIndex = current
        ? displayedRows.findIndex((row) => getRowId(row) === current.rowId)
        : -1
      const colIndex = current ? fields.indexOf(current.field) : -1

      const moveTo = (nextRow: number, nextCol: number) => {
        const row = displayedRows[nextRow]
        const field = fields[nextCol]
        if (!row || !field) return
        event.preventDefault()
        const rowId = getRowId(row)
        setFocusedCell({ rowId, field })
        if (nextRow !== rowIndex) apiValue.scrollToRow(rowId)
      }

      switch (event.key) {
        case 'ArrowDown':
          moveTo(rowIndex < 0 ? 0 : rowIndex + 1, colIndex < 0 ? 0 : colIndex)
          break
        case 'ArrowUp':
          moveTo(rowIndex - 1, colIndex < 0 ? 0 : colIndex)
          break
        case 'ArrowRight':
          moveTo(rowIndex < 0 ? 0 : rowIndex, colIndex + 1)
          break
        case 'ArrowLeft':
          moveTo(rowIndex < 0 ? 0 : rowIndex, colIndex - 1)
          break
        case 'Enter': {
          if (editMode !== 'singleClick' || !current || rowIndex < 0) break
          const column = leafColumns.find(
            (leaf) => leaf.field === current.field,
          )?.column
          if (!isCellEditable(column, rowIndex)) break
          event.preventDefault()
          startEditing(current.rowId, current.field)
          break
        }
        case 'Escape':
          setFocusedCell(null)
          break
        default:
          break
      }
    },
    [
      handleTabNavigation,
      handleArrowNavigation,
      handleEnterNavigation,
      isCellEditable,
      cellFocus,
      editingCell,
      editMode,
      focusedCell,
      leafColumns,
      displayedRows,
      getRowId,
      apiValue,
      startEditing,
    ],
  )

  // --- Scroll sync ---------------------------------------------------------

  // The header scrolls sideways only because the body tells it to, so anything
  // that moves the body without a scroll event leaves the two apart.
  const syncScrollLeft = useCallback(
    (scrollLeft: number) => {
      if (headerScrollRef.current) headerScrollRef.current.scrollLeft = scrollLeft
      if (pinnedBottomInnerRef.current) {
        pinnedBottomInnerRef.current.style.transform = `translateX(${-scrollLeft}px)`
      }
      onScrollLeft(scrollLeft)
    },
    [onScrollLeft],
  )

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      scrollLeftRef.current = event.currentTarget.scrollLeft
      syncScrollLeft(event.currentTarget.scrollLeft)
    },
    [syncScrollLeft],
  )

  useEffect(() => {
    if (apiRef) apiRef.current = apiValue
    return () => {
      if (apiRef) apiRef.current = null
    }
  }, [apiRef, apiValue])

  const { persist } = useColumnStatePersistence({
    storageKey: columnStateStorageKey,
    legacyKeys: columnStateLegacyKeys,
    unprefixed: columnStateUnprefixed,
    onRestore: (state) => apiValue.applyColumnState(state),
  })

  // Widths and visibility are written back as they change — and only then. The
  // API object is read through a ref because it is rebuilt whenever the rows
  // move, which is not a layout change worth a localStorage write.
  const apiValueRef = useRef(apiValue)
  apiValueRef.current = apiValue
  const persistedRef = useRef(false)

  useEffect(() => {
    if (!columnStateStorageKey) return
    // The first pass is the mount, and the restore has just run over it.
    if (!persistedRef.current) {
      persistedRef.current = true
      return
    }
    persist(apiValueRef.current.getColumnState())
  }, [columnStateStorageKey, persist, widthOverrides, hiddenFields])

  useEffect(() => {
    onSortChanged?.(sortModel)
  }, [onSortChanged, sortModel])

  useEffect(() => {
    onFilterChanged?.(filterModel)
  }, [onFilterChanged, filterModel])

  // The server row model owns the total; pages that report progress against it
  // ("28 of 210 reviewed") have no other way to see it.
  useEffect(() => {
    if (!isServerMode) return
    onServerCountChanged?.(server.totalCount)
  }, [isServerMode, onServerCountChanged, server.totalCount])

  // --- Filters -------------------------------------------------------------

  const handleOpenFilter = useCallback((field: string, element: HTMLElement) => {
    setFilterAnchor({ field, element })
  }, [])

  const handleFilterChange = useCallback((field: string, model: unknown) => {
    setFilterModel((prev) => {
      const next = { ...prev }
      if (model == null) delete next[field]
      else next[field] = model
      return next
    })
  }, [])

  const filterColumn = filterAnchor
    ? orderedColumns.find((col) => col.field === filterAnchor.field)
    : undefined
  const FilterComponent =
    filterColumn?.filter === 'set'
      ? SetFilterPopover
      : (filterColumn?.filter as typeof SetFilterPopover | undefined)

  // A server grid keeps its skeleton rows until the first response says how
  // many rows there really are, so the empty state cannot flash on load.
  // A scrollbar appearing does not change the viewport's border-box, so the
  // ResizeObserver above stays quiet for it. Re-measure whenever the row count
  // changes, which is when one appears or goes.
  useLayoutEffect(() => {
    const el = rootRef.current
    if (el) measureViewportRef.current(el)
  }, [displayedRows.length])

  const isEmpty = isServerMode
    ? server.hasLoadedOnce && server.totalCount === 0
    : !loading && displayedRows.length === 0

  /**
   * A first load draws shimmering rows rather than a spinner over a blank
   * grid: the list keeps its shape, so the wait reads as "this is filling in"
   * instead of "this is empty". Once there are rows to keep on screen — a
   * refetch, or a save — the overlay is the right thing again, because
   * replacing real rows with placeholders would lose the user's place.
   */
  const showSkeleton = Boolean(loading) && displayedRows.length === 0 && !isEmpty

  // The body is a different element in each of the three states (rows,
  // skeleton, empty), so every switch between them destroys the scroller and
  // mounts a fresh one at scrollLeft 0 — and a mount fires no scroll event, so
  // the header stayed where the user had scrolled it while the columns under
  // it started again from the left. A sort or a filter goes through the
  // skeleton on its way back, which is what put the wrong data under a header.
  useLayoutEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    if (scrollLeftRef.current > 0 && viewport.scrollLeft !== scrollLeftRef.current) {
      viewport.scrollLeft = scrollLeftRef.current
    }
    // Read back rather than trusting the write: a shorter row of columns
    // clamps it to its own maximum, and the header has to follow the clamped
    // value, not the asked-for one.
    scrollLeftRef.current = viewport.scrollLeft
    syncScrollLeft(viewport.scrollLeft)
  }, [showSkeleton, isEmpty, syncScrollLeft])

  const skeletonRowCount = Math.max(
    3,
    Math.min(
      MAX_SKELETON_ROWS,
      Math.floor(((boundMaxHeight ?? DEFAULT_SKELETON_HEIGHT) - headerHeight) / rowHeight),
    ),
  )

  return (
    <div
      ref={rootRef}
      className={[
        classes.grid,
        centerContent ? classes.gridCentered : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={boundMaxHeight !== undefined ? { maxHeight: boundMaxHeight } : undefined}
      data-theme-mode={isDark ? 'dark' : 'light'}
      role="grid"
      tabIndex={cellFocus ? 0 : undefined}
      // Attached whenever the grid is editable, not only for the focus ring:
      // Tab navigation lives in here too.
      onKeyDown={
        cellFocus || editMode === 'singleClick' ? handleGridKeyDown : undefined
      }
    >
      <div
        className={classes.header}
        // Wrapped header labels can need more than the nominal height — let
        // the header grow (ag's autoHeaderHeight) instead of clipping.
        style={{
          [wrapHeaderText ? 'minHeight' : 'height']:
            headerHeight + (columnGroups ? groupHeaderHeight : 0),
        }}
      >
        <div
          className={classes.headerViewport}
          ref={headerScrollRef}
          style={scrollbarWidth ? { paddingRight: scrollbarWidth } : undefined}
        >
          {columnGroups && (
            <div
              className={classes.headerGroupRow}
              style={{ width: totalWidth, height: groupHeaderHeight }}
            >
              {headerGroupBands.map((band, index) => (
                <div
                  key={`${band.key}-${index}`}
                  className={[
                    classes.headerGroupCell,
                    band.pinned === 'left' ? classes.headerPinnedLeft : '',
                    band.pinned === 'right' ? classes.headerPinnedRight : '',
                    band.headerClass ?? '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    width: band.width,
                    minWidth: band.width,
                    maxWidth: band.width,
                    ...(band.pinned === 'left' ? { left: band.offset } : {}),
                    ...(band.pinned === 'right' ? { right: band.offset } : {}),
                  }}
                  role="columnheader"
                >
                  {toHeaderCase(band.headerName)}
                </div>
              ))}
            </div>
          )}
          <div
            className={[
              classes.headerRow,
              wrapHeaderText ? classes.headerRowWrap : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              width: totalWidth,
              [wrapHeaderText ? 'minHeight' : 'height']: headerHeight,
            }}
          >
            {columnWindow.leadSpacer > 0 && (
              <div style={{ flex: '0 0 auto', width: columnWindow.leadSpacer }} aria-hidden />
            )}
            {renderedColumns.map((leaf) => (
              <GridHeaderCell
                key={leaf.field}
                column={leaf.column}
                width={leaf.width}
                sortDirection={getSortDirection(leaf.field)}
                onToggleSort={toggleSort}
                onResize={setColumnWidth}
                pinned={leaf.pinned}
                offset={leaf.offset}
                isPinnedEdge={leaf.isPinnedEdge}
                onOpenFilter={leaf.column.filter ? handleOpenFilter : undefined}
                isFilterActive={
                  filterModel[leaf.field] != null &&
                  (leaf.column.filter !== 'set' ||
                    isSetFilterActive(filterModel[leaf.field]))
                }
              />
            ))}
            {columnWindow.trailSpacer > 0 && (
              <div style={{ flex: '0 0 auto', width: columnWindow.trailSpacer }} aria-hidden />
            )}
          </div>
        </div>
      </div>

      {isEmpty ? (
        <GridEmptyState message={emptyMessage}>{emptyState}</GridEmptyState>
      ) : showSkeleton ? (
        // The scroller for as long as it is on screen, so the loading pass
        // keeps the horizontal position the rows had and hands it back to
        // them — see the scroll-restore effect above.
        <div
          ref={viewportRef}
          className={classes.body}
          onScroll={handleScroll}
          role="rowgroup"
          aria-busy="true"
        >
          <div
            style={{
              position: 'relative',
              height: skeletonRowCount * rowHeight,
              width: totalWidth,
              minWidth: '100%',
            }}
          >
            {Array.from({ length: skeletonRowCount }, (_, index) => (
              <GridSkeletonRow
                key={index}
                rowId={loadingRowId(index)}
                rowIndex={index}
                leafColumns={renderedColumns}
                leadSpacer={columnWindow.leadSpacer}
                trailSpacer={columnWindow.trailSpacer}
                // .rowVirtual is absolutely positioned for the virtualizer, so
                // each placeholder needs its own offset or they stack at top: 0.
                style={{ transform: `translateY(${index * rowHeight}px)`, height: rowHeight }}
              />
            ))}
          </div>
        </div>
      ) : (
        <GridBody
          rows={displayedRows}
          getRowId={getRowId}
          leafColumns={renderedColumns}
          leadSpacer={columnWindow.leadSpacer}
          trailSpacer={columnWindow.trailSpacer}
          api={apiValue}
          rowHeight={rowHeight}
          autoHeight={autoHeight}
          totalWidth={totalWidth}
          editingCell={editingCell}
          focusedCell={cellFocus ? focusedCell : null}
          pendingEdits={pendingEdits}
          fillHandleCell={fillHandleCell}
          onFillHandleDoubleClick={handleFillHandleDoubleClick}
          fillPreview={
            fillRange
              ? {
                  field: fillRange.field,
                  from: fillRange.fromRowIndex,
                  to: fillRange.toRowIndex,
                }
              : null
          }
          onFillHandlePointerDown={handleFillHandlePointerDown}
          rowClass={rowClass}
          onCellClick={handleCellClickWithMode}
          onCommit={handleCommit}
          onCancelEdit={stopEditing}
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
          onReachEnd={onReachEnd}
          viewportRef={viewportRef}
          isRowLoading={isServerMode ? isLoadingRow : undefined}
          onVisibleRangeChange={isServerMode ? handleVisibleRangeChange : undefined}
          treeMetaById={treeData ? tree.metaById : undefined}
          rowDrag={rowDrag}
        />
      )}

      {fillRange && fillPointer && fillRange.toRowIndex !== fillRange.fromRowIndex && (
        <div
          className={classes.fillBadge}
          style={{ left: fillPointer.x + 14, top: fillPointer.y + 14 }}
          role="status"
        >
          {Math.abs(fillRange.toRowIndex - fillRange.fromRowIndex)} row
          {Math.abs(fillRange.toRowIndex - fillRange.fromRowIndex) === 1 ? '' : 's'}
        </div>
      )}

      {filterAnchor && FilterComponent && filterColumn && (
        <Popover
          open
          anchorEl={filterAnchor.element}
          onClose={() => setFilterAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <FilterComponent
            field={filterColumn.field}
            model={filterModel[filterColumn.field] ?? null}
            rows={displayedRows as unknown[]}
            params={{
              title: filterColumn.headerName,
              ...(filterColumn.filterParams ?? {}),
            }}
            onChange={(model) => handleFilterChange(filterColumn.field, model)}
            onClose={() => setFilterAnchor(null)}
          />
        </Popover>
      )}

      {isFetchingMore && <div className={classes.loadingMore}>…</div>}

      {pinnedBottomRow && (
        <PinnedBottomRow
          row={pinnedBottomRow}
          rowId={getRowId(pinnedBottomRow)}
          leafColumns={leafColumns}
          api={apiValue}
          height={rowHeight}
          totalWidth={totalWidth}
          innerRef={pinnedBottomInnerRef}
          onCommit={handleCommit}
          onCancelEdit={stopEditing}
        />
      )}

      {(loading || pendingFill) && !showSkeleton && (
        <GridLoadingOverlay blocking={loadingBlocksInput} />
      )}
    </div>
  )
}

export default DataGrid
